import type { HttpErrorResponse } from '@angular/common/http';

import type { CorrelationId, PickActions } from '@core/ngrx-legacy';
import { createEntityCollectionStore, getEntityCollectionActions } from '@core/ngrx-legacy';
import type { PipelineTemplateItem } from '@core/pipeline-templates-types';

const idSelector = ({ id }: { id: number }) => id;
const { actions, reducer, selectors, namespace } =
  createEntityCollectionStore<PipelineTemplateItem>()('PipelineTemplateItem', {
    idSelector,
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);
      return {
        createOnePessimistic: (
          state,
          payload: {
            data: Pick<PipelineTemplateItem, 'category' | 'title'>;
          } & Required<CorrelationId>,
        ) => defaultActions.createOnePessimistic(state, payload),

        updateOnePessimistic: (
          state,
          payload: {
            data: Pick<PipelineTemplateItem, 'id' | 'category' | 'title'>;
          },
        ) => defaultActions.updateOnePessimistic(state, payload),

        duplicate: (state, payload: { id: number } & Required<CorrelationId>) =>
          adapter.markAsPending(state, { scope: null, entities: [payload.correlationId] }),
        duplicateSuccess: (
          state,
          payload: { data: PipelineTemplateItem } & Required<CorrelationId>,
        ) => defaultActions.createOnePessimisticSuccess(state, payload),
        duplicateFailed: (state, payload: { error: HttpErrorResponse } & Required<CorrelationId>) =>
          defaultActions.createOnePessimisticFailed(state, payload),

        setDefault: (state, payload: { id: number }) =>
          adapter.markAsPending(state, { scope: null, entities: [payload.id] }),
        setDefaultSuccess: (state, payload: { data: PipelineTemplateItem }) =>
          defaultActions.updateOnePessimisticSuccess(state, payload),
        setDefaultFailed: (state, payload: { error: HttpErrorResponse; data: { id: number } }) =>
          defaultActions.updateOnePessimisticFailed(state, payload),
      };
    },
  });

const PipelineTemplateItemActions: PickActions<
  typeof actions,
  | 'fetchOne'
  | 'updateOnePessimistic'
  | 'createOnePessimistic'
  | 'removeOnePessimistic'
  | 'duplicate'
  | 'setDefault'
> = actions;

export {
  PipelineTemplateItemActions,
  reducer as PipelineTemplateItemReducer,
  selectors as PipelineTemplateItemSelectors,
  namespace as PipelineTemplateItemNamespace,
};
